/**
 * Bens de uma transação: o veículo/imóvel objeto do negócio (0..N). Doação em dinheiro tem zero;
 * a compra de um carro tem um. Ligar o bem aqui faz a transação apontar para ele no grafo — é o que
 * conecta as partes "pela compra da moto XL1200". Busca um veículo/imóvel já cadastrado (ou cria na
 * hora) e vincula.
 */
import { Boxes, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ComboBuscaEntidade, type ConfigBusca, type ItemBusca } from '@/components/crud/ComboBuscaEntidade';
import { DrawerAdicionar } from '@/components/crud/DrawerAdicionar';
import { Button } from '@/components/ui/button';
import { estadoDaLista } from '@/components/ui/estado';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip } from '@/components/ui/misc';
import { api } from '@/lib/api';

type Bem = {
  bem_tipo: string;
  bem: { tipo: string; id: string; rotulo: string };
  papel: string | null;
};

const PAPEIS = [
  { valor: 'objeto', rotulo: 'Objeto' },
  { valor: 'garantia', rotulo: 'Garantia' },
  { valor: 'parte_pagamento', rotulo: 'Parte do pagamento' },
  { valor: 'permuta', rotulo: 'Permuta' },
];
const rotuloPapel = (p: string | null) => PAPEIS.find((x) => x.valor === p)?.rotulo ?? p ?? 'Bem';

const BUSCA: Record<'veiculo' | 'imovel', ConfigBusca> = {
  veiculo: {
    endpoint: '/cadastro/veiculos',
    rotulo: (v) => String(v.placa || v.modelo || 'Veículo'),
    sub: (v) => [v.marca, v.modelo].filter(Boolean).join(' ') || null,
    placeholder: 'Buscar veículo por placa, marca ou modelo…',
    camposRapido: [
      { chave: 'placa', rotulo: 'Placa', tipo: 'texto' },
      { chave: 'modelo', rotulo: 'Modelo', tipo: 'texto', obrigatorio: true },
    ],
  },
  imovel: {
    endpoint: '/cadastro/imoveis',
    rotulo: (i) => String(i.identificacao || i.matricula || 'Imóvel'),
    sub: (i) => (i.matricula ? `Matrícula ${i.matricula}` : null),
    placeholder: 'Buscar imóvel por identificação ou matrícula…',
    camposRapido: [
      { chave: 'identificacao', rotulo: 'Identificação', tipo: 'texto', obrigatorio: true },
      { chave: 'matricula', rotulo: 'Matrícula', tipo: 'texto' },
    ],
  },
};

export function PainelBensTransacao({ item }: { item: { id: string } }) {
  const transacaoId = item.id;
  const url = `/cadastro/transacoes/${transacaoId}/bens`;
  const [bens, setBens] = useState<Bem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [tipoBem, setTipoBem] = useState<'veiculo' | 'imovel'>('veiculo');
  const [alvo, setAlvo] = useState<ItemBusca | null>(null);
  const [papel, setPapel] = useState('objeto');
  const [salvando, setSalvando] = useState(false);

  async function carregar() {
    const r = await api.get<Bem[]>(url);
    setCarregando(false);
    if (r.ok) setBens(r.dados);
  }

  useEffect(() => {
    setCarregando(true);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  function limpar() {
    setAlvo(null);
    setPapel('objeto');
    setTipoBem('veiculo');
  }

  async function adicionar(): Promise<boolean> {
    if (!alvo || salvando) return false;
    setSalvando(true);
    const r = await api.post('/cadastro/transacoes/bens', {
      transacao_id: transacaoId,
      bem_tipo: tipoBem,
      bem_id: alvo.id,
      papel,
    });
    setSalvando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    toast.success('Bem vinculado à transação.');
    limpar();
    void carregar();
    return true;
  }

  async function remover(b: Bem) {
    const r = await api.del(`${url}/${b.bem_tipo}/${b.bem.id}`);
    if (!r.ok) return void toast.error(r.erro);
    toast.success('Bem removido da transação.');
    void carregar();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Boxes className="size-4 text-accent" />
          Bens
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          O veículo ou imóvel objeto do negócio. É o que aparece no grafo ligando as partes ao bem.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {estadoDaLista(carregando, bens.length === 0, 'Nenhum bem vinculado.') ?? (
          <div className="flex flex-col gap-2">
            {bens.map((b) => (
              <div
                key={`${b.bem_tipo}/${b.bem.id}`}
                className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-[15px] text-text">{b.bem.rotulo}</span>
                  <span className="tipo-corpo-pequeno capitalize text-text-tertiary">{b.bem_tipo}</span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Chip>{rotuloPapel(b.papel)}</Chip>
                  <Button
                    variant="fantasma"
                    size="sm"
                    className="text-danger hover:text-danger"
                    onClick={() => void remover(b)}
                    aria-label="Remover"
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <DrawerAdicionar
          titulo="Vincular bem"
          descricao="Busque o veículo/imóvel (ou cadastre na hora) objeto da transação."
          rotuloAbrir="Adicionar bem"
          rotuloSalvar="Vincular"
          podeSalvar={!!alvo}
          salvando={salvando}
          onSalvar={adicionar}
          onFechar={limpar}
        >
          <div className="flex flex-col gap-2">
            <span className="tipo-rotulo">Tipo de bem</span>
            <div className="flex gap-1 rounded-md bg-surface-2 p-1">
              {(['veiculo', 'imovel'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setTipoBem(t);
                    setAlvo(null);
                  }}
                  className={`flex-1 rounded-sm px-3 py-1.5 text-[13px] font-medium capitalize transition ${
                    tipoBem === t ? 'bg-surface text-text shadow-[var(--shadow-sm)]' : 'text-text-tertiary'
                  }`}
                >
                  {t === 'veiculo' ? 'Veículo' : 'Imóvel'}
                </button>
              ))}
            </div>
            <ComboBuscaEntidade key={tipoBem} config={BUSCA[tipoBem]} onSelecionar={setAlvo} />
          </div>
          <label className="flex flex-col gap-2">
            <span className="tipo-rotulo">Papel do bem</span>
            <select
              value={papel}
              onChange={(e) => setPapel(e.target.value)}
              className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
            >
              {PAPEIS.map((p) => (
                <option key={p.valor} value={p.valor}>
                  {p.rotulo}
                </option>
              ))}
            </select>
          </label>
        </DrawerAdicionar>
      </CardContent>
    </Card>
  );
}
